import React from 'react';
import { Box, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Shared visual language for the Floor Plan feature so every surface
 * (toolbar, rails, dialogs, glyphs) stays consistent.
 */
export const FP = {
  primary: '#2D7FF9',
  primaryDark: '#1B6FE6',
  primarySoft: '#EEF4FF',
  ink: '#0F172A',
  sub: '#64748B',
  faint: '#94A3B8',
  line: 'rgba(15,23,42,0.10)',
  lineSoft: 'rgba(15,23,42,0.06)',
  surface: '#F8FAFC',
  surfaceAlt: '#F1F5F9',
  danger: '#EF4444',
  dangerSoft: '#FEE2E2',
  warning: '#F59E0B',
  success: '#16A34A',
  shadowSm: '0 1px 2px rgba(15,23,42,0.06)',
  shadowMd: '0 6px 18px rgba(15,23,42,0.10)',
  shadowLg: '0 18px 48px rgba(15,23,42,0.16)',
};

export const fpCard = {
  border: `1px solid ${FP.line}`,
  borderRadius: 3,
  bgcolor: '#fff',
};

export const fpPanelHeaderSx = {
  fontWeight: 800,
  fontSize: 15,
  color: FP.ink,
  letterSpacing: '-0.01em',
};

export const fpLabelSx = {
  display: 'block',
  fontWeight: 800,
  fontSize: 11,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: FP.faint,
};

export const fpDialogPaperProps = {
  sx: {
    borderRadius: 4,
    boxShadow: FP.shadowLg,
    overflow: 'hidden',
  },
};

export const fpDialogActionsSx = {
  px: 3,
  py: 2,
  gap: 1,
  borderTop: `1px solid ${FP.lineSoft}`,
  bgcolor: FP.surface,
};

/**
 * Consistent pill/segmented selector button styling.
 */
export const pillSx = (active) => ({
  minWidth: 46,
  height: 38,
  px: 1.75,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: 14,
  textTransform: 'none',
  borderColor: active ? FP.primary : FP.line,
  color: active ? '#fff' : FP.ink,
  bgcolor: active ? FP.primary : '#fff',
  boxShadow: active ? '0 6px 14px rgba(45,127,249,0.30)' : 'none',
  '&:hover': {
    bgcolor: active ? FP.primaryDark : FP.surface,
    borderColor: active ? FP.primaryDark : FP.faint,
  },
});

export const FieldLabel = ({ children, sx }) => (
  <Typography component="span" sx={{ ...fpLabelSx, ...sx }}>
    {children}
  </Typography>
);

export const DialogHeader = ({ title, subtitle, onClose }) => (
  <DialogTitle sx={{ px: 3, pt: 2.5, pb: subtitle ? 1.5 : 2 }}>
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
    >
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 20, color: FP.ink }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ color: FP.sub, fontSize: 13, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {onClose ? (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            mt: -0.5,
            mr: -0.75,
            color: FP.faint,
            '&:hover': { color: FP.ink, bgcolor: FP.surface },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Stack>
  </DialogTitle>
);
